/**
 * Vincent B907 (Eric 2026-02-14 SPEC STRICK PROD) — Page admin
 * « Analytics de tags » : quels tags Vincent et son équipe recherchent
 * le plus. Sélecteur de fenêtre (7d / 30d / 90d) + top 20 en bar chart
 * horizontal + tendance (%) vs période précédente.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const RANGES = [
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "90d", label: "90 jours" },
];

// Vincent B927b (Eric 2026-02-14 SPEC STRICK PROD) — Storage key pour
// mémoriser le count de nudges vu par l'utilisateur entre les sessions.
// Utilisé pour toaster UNIQUEMENT quand une NOUVELLE collision apparaît.
const _NUDGE_SEEN_KEY = "knoria.tagNudgesSeenCount";

export default function TagAnalyticsAdminPage() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Vincent B927b — Nudges live-fetchés + toast alerte quand un nouveau
  // apparaît vs le dernier snapshot vu par l'utilisateur.
  const [nudges, setNudges] = useState([]);
  // Vincent B928 (Eric 2026-02-14 SPEC STRICK PROD) — Historique des
  // fusions appliquées + rollback en un clic.
  const [history, setHistory] = useState([]);
  const _lastNotifiedRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await axios.get(
        `${API}/api/admin/tag-analytics`,
        { params: { range }, withCredentials: true },
      );
      setData(payload);
    } catch (err) {
      toast.error("Erreur : " + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [range]);

  // Vincent B927b — Charge les nudges au mount + polling toutes les 60 s.
  // Compare le count vs le dernier compte VU (persisté en localStorage)
  // et toast si de NOUVELLES paires quasi-identiques sont détectées.
  const loadNudges = useCallback(async () => {
    try {
      const { data: nd } = await axios.get(
        `${API}/api/tags/merge-nudges`,
        { withCredentials: true },
      );
      const list = nd?.nudges || [];
      setNudges(list);
      const currentCount = list.length;
      const seen = Number(localStorage.getItem(_NUDGE_SEEN_KEY) || "0");
      // Toaster uniquement si :
      //   • Le count a AUGMENTÉ vs le dernier snapshot vu
      //   • Et on n'a pas déjà notifié CE count (évite les doublons de
      //     toast au sein d'une même session).
      if (currentCount > seen && currentCount !== _lastNotifiedRef.current) {
        _lastNotifiedRef.current = currentCount;
        const delta = currentCount - seen;
        toast.warning(
          `🔀 ${delta} nouvelle(s) collision(s) de tags détectée(s). ${currentCount} paire(s) à fusionner au total.`,
          {
            duration: 12000,
            action: {
              label: "Voir",
              onClick: () => {
                localStorage.setItem(_NUDGE_SEEN_KEY, String(currentCount));
                document
                  .querySelector('[data-testid="tag-analytics-nudges-section"]')
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              },
            },
          },
        );
      } else if (currentCount === 0 && seen > 0) {
        // Reset le compteur vu quand tout est propre — évite un toast erroné
        // au prochain nudge qui apparaîtra.
        localStorage.setItem(_NUDGE_SEEN_KEY, "0");
      }
    } catch {
      // silencieux — pas de nudges = section masquée, pas d'erreur
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Vincent B928 (Eric 2026-02-14 SPEC STRICK PROD) — Historique fusions.
  const loadHistory = useCallback(async () => {
    try {
      const { data: h } = await axios.get(
        `${API}/api/tags/merge-history`,
        { withCredentials: true },
      );
      setHistory(h?.history || []);
    } catch {
      // silencieux
    }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleRollback = async (mergeId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Annuler cette fusion ? Le variant sera réintroduit sur les vidéos concernées.")) return;
    try {
      const { data } = await axios.post(
        `${API}/api/tags/merge-history/${mergeId}/rollback`,
        {},
        { withCredentials: true },
      );
      toast.success(`Fusion annulée : « ${data.restored_variant} » remis sur ${data.restored_count} vidéo(s).`);
      loadHistory();
    } catch (e) {
      toast.error("Rollback échoué : " + (e?.response?.data?.detail || e?.message));
    }
  };

  // Vincent B927b — Polling nudges (60 s) + refresh au focus de la page.
  useEffect(() => {
    loadNudges();
    const iv = setInterval(loadNudges, 60000);
    const onFocus = () => loadNudges();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadNudges]);

  // Marque les nudges comme « vus » quand la section est réellement
  // affichée à l'écran — évite les toasts répétés.
  const markNudgesAsSeen = useCallback(() => {
    if (nudges.length > 0) {
      localStorage.setItem(_NUDGE_SEEN_KEY, String(nudges.length));
    }
  }, [nudges.length]);

  const top = data?.top || [];
  const maxCount = top.length > 0 ? Math.max(...top.map((t) => t.count)) : 1;

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="tag-analytics-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">📊 Analytics des tags recherchés</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Quels tags votre équipe recherche le plus. Purge auto à 90 jours (GDPR-friendly).
          </p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-zinc-300 rounded p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`text-[11px] font-semibold px-2 py-1 rounded transition-colors ${
                range === r.key
                  ? "bg-[#002FA7] text-white"
                  : "text-zinc-700 hover:bg-zinc-100"
              }`}
              data-testid={`tag-analytics-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3 bg-zinc-50 border border-zinc-200 rounded p-3 flex items-center gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Total recherches</p>
          <p className="text-2xl font-bold text-zinc-900 tabular-nums" data-testid="tag-analytics-total">
            {loading ? "…" : (data?.total_events ?? 0)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Tags uniques (top)</p>
          <p className="text-2xl font-bold text-zinc-900 tabular-nums">
            {loading ? "…" : top.length}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500 italic text-center py-8">⏳ Chargement…</p>
      ) : top.length === 0 ? (
        <p className="text-sm text-zinc-500 italic text-center py-8 bg-white border border-zinc-200 rounded" data-testid="tag-analytics-empty">
          Aucune recherche par tag dans cette période. Les analytics apparaîtront dès que Vincent (ou son équipe) utilisera la barre de recherche.
        </p>
      ) : (
        <ol className="space-y-1.5" data-testid="tag-analytics-list">
          {top.map((row, i) => (
            <li
              key={row.tag}
              className="flex items-center gap-3 bg-white border border-zinc-200 rounded p-2"
              data-testid={`tag-analytics-row-${row.tag}`}
            >
              <span className="text-[10px] font-mono text-zinc-400 w-6 text-right">#{i + 1}</span>
              <span className="text-xs font-medium text-zinc-900 truncate w-28" title={row.tag}>
                {row.tag}
              </span>
              <div className="flex-1 h-4 bg-zinc-100 rounded relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-[#002FA7]"
                  style={{ width: `${(row.count / maxCount) * 100}%` }}
                  data-testid={`tag-analytics-bar-${row.tag}`}
                />
              </div>
              <span className="text-xs font-mono text-zinc-800 tabular-nums w-12 text-right" data-testid={`tag-analytics-count-${row.tag}`}>
                {row.count}
              </span>
              <span
                className={`text-[10px] font-semibold w-14 text-right ${
                  row.trend_pct === null
                    ? "text-violet-600"
                    : row.trend_pct > 0
                      ? "text-teal-700"
                      : row.trend_pct < 0
                        ? "text-red-700"
                        : "text-zinc-500"
                }`}
                data-testid={`tag-analytics-trend-${row.tag}`}
                title={row.trend_pct === null ? "Nouveau ce range" : `Évolution vs période précédente`}
              >
                {row.trend_pct === null
                  ? "NEW"
                  : (row.trend_pct > 0 ? "▲" : row.trend_pct < 0 ? "▼" : "•") + " " + Math.abs(row.trend_pct) + "%"}
              </span>
            </li>
          ))}
        </ol>
      )}

      {/* Vincent B927b (Eric 2026-02-14 SPEC STRICK PROD) — Section
          « Collisions de tags à fusionner ». Alimentée par
          /api/tags/merge-nudges. Toast d'alerte affiché quand une
          nouvelle paire apparaît vs le dernier compte vu (localStorage). */}
      {nudges.length > 0 && (
        <section
          className="mt-8 border border-rose-200 bg-rose-50 rounded-lg p-4"
          data-testid="tag-analytics-nudges-section"
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-rose-900">
              🔀 Collisions à fusionner
              <span className="ml-2 text-[11px] font-normal text-rose-700" data-testid="tag-analytics-nudges-count">
                · {nudges.length} paire(s)
              </span>
            </h2>
            <button
              type="button"
              onClick={markNudgesAsSeen}
              className="text-[10px] font-semibold px-2 py-1 rounded bg-white border border-rose-300 text-rose-800 hover:bg-rose-100"
              data-testid="tag-analytics-nudges-mark-seen-btn"
              title="Marquer les collisions actuelles comme vues (arrête l'alerte tant qu'aucune nouvelle n'apparaît)"
            >
              Marquer comme vu
            </button>
          </div>
          <p className="text-[11px] text-rose-800 mb-2">
            Les paires ci-dessous sont probablement des variantes du même concept. Ouvrez la fiche d'un document et cliquez sur la chip Étiquettes pour appliquer la fusion sur toute l'organisation.
          </p>
          <ul className="space-y-1 max-h-72 overflow-y-auto" data-testid="tag-analytics-nudges-list">
            {nudges.slice(0, 30).map((n) => (
              <li
                key={`${n.canonical}::${n.variant}`}
                className="flex items-center gap-2 text-xs bg-white border border-rose-200 rounded px-2 py-1"
                data-testid={`tag-analytics-nudge-${n.canonical}-${n.variant}`}
                data-reason={n.reason}
              >
                <span className="line-through text-zinc-500 flex-shrink-0" title={`${n.variant_count}× d'usage`}>
                  {n.variant}
                </span>
                <span className="text-rose-600 flex-shrink-0">→</span>
                <span className="font-semibold text-teal-800 flex-shrink-0" title={`${n.canonical_count}× d'usage`}>
                  {n.canonical}
                </span>
                <span className="ml-auto text-[9px] text-zinc-400 flex-shrink-0">
                  {n.reason === "variante_ponctuation"
                    ? "variante"
                    : `~${Math.round(n.similarity * 100)}%`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Vincent B928 (Eric 2026-02-14 SPEC STRICK PROD) — Historique
          des fusions appliquées + bouton Rollback par ligne.
          Section masquée quand aucune fusion n'a jamais été appliquée. */}
      {history.length > 0 && (
        <section
          className="mt-8 border border-zinc-200 bg-zinc-50 rounded-lg p-4"
          data-testid="tag-analytics-merge-history-section"
        >
          <h2 className="text-sm font-bold text-zinc-900 mb-2">
            📜 Historique des fusions
            <span className="ml-2 text-[11px] font-normal text-zinc-600" data-testid="tag-analytics-merge-history-count">
              · {history.length} entrée(s)
            </span>
          </h2>
          <p className="text-[11px] text-zinc-600 mb-2">
            50 dernières fusions de tags appliquées sur l'organisation. Cliquer sur « Annuler » remet le variant sur les vidéos concernées (idempotent).
          </p>
          <ul className="space-y-1 max-h-72 overflow-y-auto" data-testid="tag-analytics-merge-history-list">
            {history.map((h) => (
              <li
                key={h.id}
                className={`flex items-center gap-2 text-xs bg-white border rounded px-2 py-1 ${
                  h.rolled_back ? "border-zinc-200 opacity-60" : "border-zinc-300"
                }`}
                data-testid={`tag-analytics-merge-history-item-${h.id}`}
                data-rolled-back={h.rolled_back ? "true" : "false"}
              >
                <span className="line-through text-zinc-500 flex-shrink-0" title={`${h.affected_count} vidéo(s)`}>
                  {h.variant}
                </span>
                <span className="text-zinc-500 flex-shrink-0">→</span>
                <span className="font-semibold text-teal-800 flex-shrink-0">
                  {h.canonical}
                </span>
                <span className="ml-auto text-[10px] text-zinc-500 flex-shrink-0">
                  {h.affected_count} vid · {(h.applied_at || "").slice(0, 16).replace("T", " ")}
                </span>
                {h.rolled_back ? (
                  <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded bg-zinc-200 text-zinc-600"
                    data-testid={`tag-analytics-merge-history-rolled-back-${h.id}`}
                  >
                    Annulée
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleRollback(h.id)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded bg-white border border-rose-300 text-rose-800 hover:bg-rose-50"
                    data-testid={`tag-analytics-merge-history-rollback-${h.id}`}
                    title="Annuler cette fusion et remettre le variant sur les vidéos concernées"
                  >
                    Annuler
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
